import httpRequest from "@/auth/http-client";
import { ISegmentData } from "@/components/screens/CampaignFormPage/types";
import { buildQueryParams } from "@/helpers/build-params";
import { buildPaginationQuery } from "@/helpers/request-helpers";
import { ICampaign, ICampaignDiscount, ICampaignInput } from "@/interfaces/campaign.interface";
import IContact from "@/interfaces/contact.interface";
import { IFilter } from "@/types/filter.types";
import { PaginatedResponse, RequestPagination } from "@/types/requests.types";

class CampaignService {
  private baseUrl = "api/v1/campaign";

  async getAll(pagination: RequestPagination): Promise<PaginatedResponse<ICampaign>> {
    const paginationQuery = buildPaginationQuery(pagination);
    const campaigns = await httpRequest.get(`${this.baseUrl}?${paginationQuery}`);
    return campaigns;
  }

  async filter(
    filter: IFilter<ICampaign>[],
    pagination: RequestPagination
  ): Promise<PaginatedResponse<ICampaign>> {
    const paginationQuery = buildPaginationQuery(pagination);

    const response = await httpRequest.post(`${this.baseUrl}/filter?${paginationQuery}`, {
      filter,
    });

    return response;
  }

  async create(formData: ICampaignInput | FormData): Promise<ICampaign> {
    try {
      const config =
        typeof FormData !== "undefined" && formData instanceof FormData
          ? {
              headers: {
                "Content-Type": "multipart/form-data",
              },
            }
          : undefined;

      return await httpRequest.post<ICampaign>(this.baseUrl, formData as never, config);
    } catch (error: any) {
      // Extrai mensagem de erro de forma robusta
      const rawData = error?.response?.data;
      const message: string =
        typeof rawData === "string"
          ? rawData
          : rawData?.message ||
            rawData?.title ||
            JSON.stringify(rawData || {}) ||
            String(error?.message || "");

      throw error;
    }
  }

  async getById(campaignId: number): Promise<ICampaign> {
    const campaign = await httpRequest.get(`${this.baseUrl}/${campaignId}`);

    return campaign;
  }

  async getDiscount(campaignId: number, skuId: number): Promise<ICampaignDiscount | null> {
    return httpRequest.get(`${this.baseUrl}/${campaignId}/${skuId}`);
  }

  async update(formData: ICampaignInput, campaignId: number): Promise<void> {
    await httpRequest.put(`${this.baseUrl}/${campaignId}`, formData);
  }

  async delete(campaignId: number): Promise<void> {
    await httpRequest.delete(`${this.baseUrl}/${campaignId}`);
  }

  async deleteItem(campaign: number, itemId: number): Promise<void> {
    await httpRequest.delete(`${this.baseUrl}/${campaign}/item/${itemId}`);
  }

  async getContacts(
    formData: ISegmentData | null,
    pagination: RequestPagination
  ): Promise<PaginatedResponse<IContact>> {
    const paginationQuery = buildPaginationQuery(pagination);
    const formQuery = buildQueryParams(formData);

    const response = await httpRequest.get(
      `${this.baseUrl}/contact/segment?${formQuery}&${paginationQuery}`
    );

    return response;
  }
}

const campaignService = new CampaignService();
export default campaignService;
